import { supabase } from '@/lib/supabase';

export interface CallRecord {
  id: string;
  userId: string;
  userName: string;
  phoneNumber: string;
  habitTypeId: string;
  habitName: string;
  habitIcon: string;
  direction: 'outbound' | 'inbound';
  status: 'queued' | 'ringing' | 'answered' | 'no_answer' | 'busy' | 'failed' | 'voicemail';
  scheduledAt: string;
  startedAt: string | null;
  endedAt: string | null;
  durationSeconds: number | null;
  dtmfInput: string | null;
  dtmfExpected: string;
  recordingUrl: string | null;
  retryCount: number;
  deltaMinutes: number | null;
}

export interface CallFilterOption {
  label: string;
  value: string;
}

export interface DtmfLogEntry {
  id: string;
  callId: string;
  digit: string;
  pressedAt: string;
  menuPath: string;
  action: string;
}

export interface CallSummary {
  totalCalls: number;
  todayCalls: number;
  answeredRate: number;
  avgDuration: number;
  voicemailRate: number;
  missedRate: number;
  totalDtmfDigits: number;
}

export const callStatusOptions: CallFilterOption[] = [
  { label: 'All Statuses', value: 'all' },
  { label: 'Answered', value: 'answered' },
  { label: 'Voicemail', value: 'voicemail' },
  { label: 'No Answer', value: 'no_answer' },
  { label: 'Busy', value: 'busy' },
  { label: 'Failed', value: 'failed' },
  { label: 'Queued', value: 'queued' },
];

export const callDirectionOptions: CallFilterOption[] = [
  { label: 'All Directions', value: 'all' },
  { label: 'Outbound', value: 'outbound' },
  { label: 'Inbound', value: 'inbound' },
];

interface PhoneCallRow {
  id: string;
  user_id: string;
  daily_record_id: string | null;
  direction: 'outbound' | 'inbound';
  status: CallRecord['status'];
  scheduled_at: string | null;
  started_at: string | null;
  ended_at: string | null;
  duration_seconds: number | null;
  dtmf_input: string | null;
  recording_url: string | null;
  signalwire_call_sid: string | null;
  created_at: string;
  daily_records: {
    habit_type_id: string;
    delta_minutes: number | null;
    habit_types: { name: string; calendar_icon: string | null } | null;
  } | null;
}

export interface CallLogResult {
  calls: CallRecord[];
  habitOptions: CallFilterOption[];
  dtmfLogs: DtmfLogEntry[];
  summary: CallSummary;
}

const dtmfActions: Record<string, string> = {
  '1': 'Confirmed completion',
  '2': 'Snoozed reminder',
  '3': 'Skipped today',
  '9': 'Repeated menu',
};

function buildDtmfLogs(calls: CallRecord[]): DtmfLogEntry[] {
  const logs: DtmfLogEntry[] = [];
  for (const call of calls) {
    if (!call.dtmfInput) continue;
    [...call.dtmfInput].forEach((digit, idx) => {
      logs.push({
        id: `${call.id}-dtmf-${idx}`,
        callId: call.id,
        digit,
        pressedAt: call.endedAt || call.startedAt || call.scheduledAt,
        menuPath: 'Main Menu',
        action: dtmfActions[digit] || 'Input received',
      });
    });
  }
  return logs;
}

function buildSummary(calls: CallRecord[], dtmfLogs: DtmfLogEntry[]): CallSummary {
  const total = calls.length;
  const todayStr = new Date().toDateString();
  const todayCalls = calls.filter((c) => new Date(c.scheduledAt).toDateString() === todayStr).length;
  const answered = calls.filter((c) => c.status === 'answered').length;
  const voicemail = calls.filter((c) => c.status === 'voicemail').length;
  const missed = calls.filter((c) => c.status === 'no_answer' || c.status === 'busy' || c.status === 'failed').length;
  const durations = calls.filter((c) => c.durationSeconds != null).map((c) => c.durationSeconds as number);
  const avgDuration = durations.length ? Math.round(durations.reduce((a, b) => a + b, 0) / durations.length) : 0;
  const pct = (n: number) => (total ? Math.round((n / total) * 100) : 0);
  return {
    totalCalls: total,
    todayCalls,
    answeredRate: pct(answered),
    avgDuration,
    voicemailRate: pct(voicemail),
    missedRate: pct(missed),
    totalDtmfDigits: dtmfLogs.length,
  };
}

/** Fetch the current user's call log from Supabase, newest first. */
export async function fetchCallLogs(limit = 200): Promise<CallLogResult> {
  const { data: { session } } = await supabase.auth.getSession();
  const user = session?.user;
  const emptyResult: CallLogResult = {
    calls: [],
    habitOptions: [{ label: 'All Habits', value: 'all' }],
    dtmfLogs: [],
    summary: buildSummary([], []),
  };
  if (!user) return emptyResult;

  const [callsRes, profileRes] = await Promise.all([
    supabase
      .from('phone_calls')
      .select('id, user_id, daily_record_id, direction, status, scheduled_at, started_at, ended_at, duration_seconds, dtmf_input, recording_url, signalwire_call_sid, created_at, daily_records(habit_type_id, delta_minutes, habit_types(name, calendar_icon))')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(limit),
    supabase
      .from('users')
      .select('full_name, phone_number')
      .eq('id', user.id)
      .maybeSingle(),
  ]);

  if (callsRes.error) {
    console.error('Failed to load call logs:', callsRes.error);
    return emptyResult;
  }

  const rows = (callsRes.data || []) as unknown as PhoneCallRow[];
  const userName = profileRes.data?.full_name || user.email || '';
  const phoneNumber = profileRes.data?.phone_number || '';

  const habitSet = new Map<string, string>();
  const calls: CallRecord[] = rows.map((r) => {
    const habitTypeId = r.daily_records?.habit_type_id || '';
    const habitName = r.daily_records?.habit_types?.name || 'Test Call';
    const habitIcon = r.daily_records?.habit_types?.calendar_icon || 'ri-phone-line';
    if (habitTypeId) habitSet.set(habitTypeId, habitName);
    return {
      id: r.id,
      userId: r.user_id,
      userName,
      phoneNumber,
      habitTypeId,
      habitName,
      habitIcon,
      direction: r.direction,
      status: r.status,
      scheduledAt: r.scheduled_at || r.created_at,
      startedAt: r.started_at,
      endedAt: r.ended_at,
      durationSeconds: r.duration_seconds,
      dtmfInput: r.dtmf_input,
      dtmfExpected: '1',
      recordingUrl: r.recording_url,
      retryCount: 0,
      deltaMinutes: r.daily_records?.delta_minutes ?? null,
    };
  });

  const habitOptions: CallFilterOption[] = [
    { label: 'All Habits', value: 'all' },
    ...[...habitSet.entries()].map(([value, label]) => ({ label, value })),
  ];

  const dtmfLogs = buildDtmfLogs(calls);
  return { calls, habitOptions, dtmfLogs, summary: buildSummary(calls, dtmfLogs) };
}
